import React, { useState } from 'react';
import {
  Button,
  Column,
  Copy,
  Flex,
  Row,
  Stack,
  SvgPencil,
  SvgX,
} from '@grubhubprod/gh-cookbook-react';
import { Square } from '@grubhubprod/gh-cookbook-react/dist';
import { Address } from '@grubhubprod/order-taking-client-sdk/dist/sdk/types/geocode';
import { useParams } from 'react-router-dom';
import { ILocation } from 'src/types/Location';
import {
  SHORT_SEARCH_DELAY,
  useLocationSearch,
} from 'src/utilities/useSearchHooks';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { sdk } from 'src/utilities/sdk';
import { pushError, pushSuccess } from 'src/utilities/notifications';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import {
  EnterpriseSetupTable,
  IEnterpriseSetupTableRowProps,
} from 'src/components/EnterpriseSetupTable/EnterpriseSetupTable';
import { AddressConfirmationModal } from 'src/components/AddressConfirmationModal/AddressConfirmationModal';
import { EnterpriseLocationModal } from 'src/components/EnterpriseSignupSteps/EnterpriseCompanyDetailsStep/EnterpriseLocationModal';

interface EnterpriseLocationTableProps {
  hasItemsCb?: (isValid: boolean) => void;
}

export function EnterpriseLocationTable({
  hasItemsCb,
}: EnterpriseLocationTableProps): JSX.Element {
  const { corpid } = useParams<{ corpid: string }>();

  const { isDesktop } = useScreenSizes();
  const [addressConfirmationModal, setAddressConfirmationModal] = useState({
    showModal: false,
    suggestedGeoAddresses: [],
    location: null,
    currentAddressString: '',
    toggleState: null,
    isEditing: false,
  });

  const [addressConfirmedData, setAddressConfirmedData] = useState(null);
  const [wasAddressConfirmed, setWasAddressConfirmed] = useState(false);

  function showAddressConfirmationModal(
    location: ILocation,
    suggestedGeoAddresses: Address[],
    toggleState: () => void,
    isEditing
  ) {
    setAddressConfirmationModal({
      currentAddressString: `${location.street_address1}, ${location.address_locality}, ${location.address_region}, ${location.postal_code}`,
      location,
      suggestedGeoAddresses,
      showModal: true,
      toggleState,
      isEditing,
    });
  }

  function onAddressConfirmationClose() {
    const isEditing = addressConfirmationModal.isEditing;
    addressConfirmationModal.toggleState(
      isEditing ? addressConfirmationModal.location.id : true
    );
    setAddressConfirmedData({ ...addressConfirmationModal.location });
    setAddressConfirmationModal({
      showModal: false,
      location: null,
      suggestedGeoAddresses: null,
      toggleState: null,
      currentAddressString: '',
      isEditing: false,
    });
  }

  function onAddressConfirmationSuccess(address: Address) {
    const isEditing = addressConfirmationModal.isEditing;
    const location: ILocation = addressConfirmationModal.location;
    setAddressConfirmedData({
      ...location,
      street_address1: address.street_address1,
      street_address2: address.street_address2,
      address_locality: address.address_locality,
      address_region: address.address_region,
      postal_code: address.postal_code,
      cross_streets: '',
      latitude: address.latitude,
      longitude: address.longitude,
    });
    addressConfirmationModal.toggleState(isEditing ? location.id : true);
    setAddressConfirmationModal({
      showModal: false,
      location: null,
      suggestedGeoAddresses: null,
      toggleState: null,
      currentAddressString: '',
      isEditing: false,
    });
    setWasAddressConfirmed(true);
  }

  return (
    <Stack y="md">
      <EnterpriseSetupTable<ILocation>
        corpId={corpid as string}
        searchPlaceholder="Search by location name or Zip code"
        noResultsMessage="You haven’t added any locations yet"
        addButtonText="Add location"
        searchResource={useLocationSearch}
        tableMaxHeight={isDesktop ? '205px' : 'initial'}
        hasItemsCb={hasItemsCb}
        headerColumns={[
          {
            columnName: 'Location name',
            columnWidth: 4,
          },
          {
            columnName: 'Address',
            columnWidth: 7,
            gutter: 8,
          },
        ]}
        rowElementRender={(
          location,
          isEditing,
          toggleEditState,
          updateSearch
        ) => (
          <LocationTableRow
            location={location}
            isEditing={isEditing}
            toggleEditState={(id, clearAddressConfirmedData) => {
              toggleEditState(id);
              if (clearAddressConfirmedData) {
                setAddressConfirmedData(null);
              }
            }}
            updateSearch={updateSearch}
            corpid={corpid as string}
            existingFormData={
              addressConfirmedData && addressConfirmedData.id === location.id
                ? addressConfirmedData
                : null
            }
            onConfirmAddressCb={(
              location,
              suggestedGeoAddresses,
              toggleEditState
            ) => {
              showAddressConfirmationModal(
                location,
                suggestedGeoAddresses,
                toggleEditState,
                true
              );
              toggleEditState();
            }}
            wasAddressConfirmed={wasAddressConfirmed}
          />
        )}
        footerAddItemRender={(toggleAddState, updateSearch) => (
          <EnterpriseLocationModal
            corpId={corpid as string}
            existingFormData={addressConfirmedData}
            onSuccess={() => {
              updateSearch();
              toggleAddState();
              setAddressConfirmedData(null);
              setWasAddressConfirmed(false);
            }}
            closeModal={() => {
              toggleAddState();
              setAddressConfirmedData(null);
            }}
            onConfirmAddressCb={(location, suggestedGeoAddresses) => {
              showAddressConfirmationModal(
                location,
                suggestedGeoAddresses,
                toggleAddState,
                false
              );
              toggleAddState();
            }}
            wasAddressConfirmed={wasAddressConfirmed}
          />
        )}
      />
      {addressConfirmationModal.showModal && (
        <AddressConfirmationModal
          currentAddressString={addressConfirmationModal.currentAddressString}
          suggestedGeoAddresses={addressConfirmationModal.suggestedGeoAddresses}
          onSuccess={onAddressConfirmationSuccess}
          onClose={onAddressConfirmationClose}
        />
      )}
    </Stack>
  );
}

interface ILocationTableRow extends IEnterpriseSetupTableRowProps {
  location: ILocation;
  corpid: string;
  onConfirmAddressCb?: (
    fields: ILocation,
    uniqueSuggestedGeoAddresses: Address[],
    toggleEditState: (id?: string) => void
  ) => void;
  existingFormData?: ILocation;
  toggleEditState: (id?: string, clearAddressConfirmedData?: boolean) => void;
  wasAddressConfirmed?: boolean;
}

function LocationTableRow({
  location,
  isEditing,
  toggleEditState,
  updateSearch,
  corpid,
  onConfirmAddressCb,
  existingFormData,
  wasAddressConfirmed,
}: ILocationTableRow): JSX.Element {
  const [locationDeletionInProgress, setLocationDeletionInProgress] =
    useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  function doSearch() {
    updateSearch({ page: 1 });
    toggleEditState();
  }
  async function deleteLocation(id: string): Promise<void> {
    setLocationDeletionInProgress(true);
    try {
      await sdk.http.delete(`/corp-admin/${corpid}/locations/${id}`);
      setTimeout(() => {
        pushSuccess(`Your location was removed`);
        doSearch();
        setShowConfirmationModal(false);
      }, SHORT_SEARCH_DELAY);
    } catch (error) {
      pushError(`Your location was not removed. Try again.`);
      setShowConfirmationModal(false);
      setLocationDeletionInProgress(false);
    }
  }
  return (
    <>
      <Square data-testid={`location-row-${location.id}`} inset="sm">
        <Row gutter={8}>
          <Column gutter={8} phone={4}>
            {location.name}
          </Column>
          <Column gutter={8} phone={6}>
            <Copy variant="body">
              {location.street_address1}
              {location.street_address2
                ? ` ${location.street_address2}`
                : ''}, {location.address_locality} {location.address_region}{' '}
              {location.postal_code}
            </Copy>
          </Column>
          <Column gutter={8} phone={2}>
            <Flex
              width="100%"
              height="100%"
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Stack x="md">
                <Tooltip
                  roundedContainer
                  content={
                    <Copy variant="body" color="invertedPrimaryText">
                      Edit
                    </Copy>
                  }
                >
                  <div>
                    <Button
                      variant="tertiary"
                      className="tableActionButtons"
                      onClick={() => toggleEditState(location.id, true)}
                    >
                      <SvgPencil aria-label="Edit" />
                    </Button>
                  </div>
                </Tooltip>
              </Stack>
              <Tooltip
                roundedContainer
                content={
                  <Copy variant="body" color="invertedPrimaryText">
                    Delete
                  </Copy>
                }
              >
                <div>
                  <Button
                    variant="tertiary"
                    className="tableActionButtons"
                    onClick={() => setShowConfirmationModal(true)}
                  >
                    <SvgX aria-label="Remove" />
                  </Button>
                </div>
              </Tooltip>
            </Flex>
          </Column>
        </Row>
      </Square>
      {showConfirmationModal && (
        <SimpleDialog
          id={location.id}
          primaryActionFlavor="danger"
          title={`Remove "${location.name}"`}
          description="You’re about to remove this location."
          primaryActionText="Yes, remove"
          isPrimaryButtonLoading={locationDeletionInProgress}
          buttonsDirection="row"
          secondaryActionText="Cancel"
          disablePrimaryActionButton={locationDeletionInProgress}
          disableSecondaryActionButton={locationDeletionInProgress}
          onSecondaryActionClick={() => {
            setShowConfirmationModal(false);
          }}
          onPrimaryActionClick={(id) => deleteLocation(id)}
        />
      )}

      {isEditing && (
        <EnterpriseLocationModal
          corpId={corpid}
          locationId={location.id}
          existingFormData={existingFormData}
          onConfirmAddressCb={(fields, uniqueSuggestedGeoAddresses) => {
            onConfirmAddressCb(
              fields,
              uniqueSuggestedGeoAddresses,
              toggleEditState
            );
          }}
          onSuccess={() => {
            doSearch();
            toggleEditState(undefined, true);
          }}
          closeModal={() => toggleEditState(undefined, true)}
          wasAddressConfirmed={wasAddressConfirmed}
        />
      )}
    </>
  );
}
